// aiChatClientV4.ts - Version 4 client for process-query-v3 endpoint

import { MatchItemV4Imperial } from "./aiChatClientV4Imperial";
import { MatchItemV4Metric } from "./aiChatClientV4Metric";

/**
 * Represents a single message in the chat history for V4.
 */
export interface ChatMessageV4 {
  uuid: string | undefined;
  role: "user" | "assistant" | "system";
  content: string;
  rawContent?: string;
  // In V4, result stored in the message will be a converted structure for UI highlighting
  result?: any;
  metadata?: {
    searchMode: "global" | "current";
    selectedSpaces?: string[];
    planInfo?: {
      propertyName: string;
      planName: string;
    };
    timestamp?: string;
  };
}

/**
 * Request body expected by the V4 endpoint.
 */
export interface ChatRequestBodyV4 {
  query: string;
  search_type: "hybrid" | "semantic" | "keyword";
  max_results: number;
}

export type { MatchItemV4Imperial, MatchItemV4Metric };

export type MatchItemV4 = MatchItemV4Metric | MatchItemV4Imperial;

export type QueryInterpretationV4 = MatchItemV4[];

export interface ResponsePayloadV4 {
  message: string;
  query_interpretation: QueryInterpretationV4;
  exact_matches: MatchItemV4[];
  partial_matches: MatchItemV4[];
}

export interface ChatSuccessResponseV4 {
  response: ResponsePayloadV4;
  success: boolean;
  query: string;
}

export interface BackendErrorResponseV4 {
  error: string;
  details?: string;
}

/**
 * Calls the backend AI chat V4 API endpoint.
 *
 * @param apiUrl - Full URL (e.g., 'http://localhost:8000/process-query-v3').
 * @param userPrompt - The user's current message.
 * @param searchType - Search strategy, defaults to 'hybrid'.
 * @param maxResults - Maximum number of results, defaults to 20.
 */
export async function callChatApiV4(
  apiUrl: string,
  userPrompt: string,
  searchType: "hybrid" | "semantic" | "keyword" = "hybrid",
  maxResults: number = 20
): Promise<ChatSuccessResponseV4> {
  const requestBody: ChatRequestBodyV4 = {
    query: userPrompt,
    search_type: searchType,
    max_results: maxResults,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorData: BackendErrorResponseV4 = {
        error: `HTTP error! Status: ${response.status}`,
      };
      try {
        errorData = await response.json();
      } catch (e) {
        console.warn("Could not parse error response body as JSON.");
      }
      const errorMessage = errorData.details
        ? `${errorData.error} - Details: ${errorData.details}`
        : errorData.error;
      throw new Error(errorMessage);
    }

    const data: ChatSuccessResponseV4 = await response.json();
    if (!data || typeof data !== "object") {
      throw new Error("Invalid response structure received from the API.");
    }
    return data;
  } catch (error) {
    console.error("Error calling chat API V4:", error);
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("An unknown error occurred during the API call.");
    }
  }
}
